/**
 * Workflow store — React/Zustand mirror of the Vue ERP store function
 * `getWorkFlowDetails()` in `src/stores/encrypt-decrypt.ts` line 62.
 *
 * Layering follows the Vue pattern exactly:
 *
 *   api/workflowApi.ts   → raw `response.data` (no unwrap)
 *   stores/workflow-store.ts (this file)
 *      → unwraps to `body.data.workflowDetails` and caches `body.data`
 *        in the SHARED `LocalCache.workFlow` IndexedDB store, matching
 *        the Vue ERP's exact format. Both apps read each other's writes.
 *   features/.../page    → `await getWorkFlowDetails()`
 *
 * Cache format (matches Vue `permission.store.ts:89-97`):
 *   IDB database: `LocalCache`
 *   Object store: `workFlow`
 *   Entry:        { id: 'data', value: <body.data> }
 *                  ── i.e. { id: 'data', value: { workflowDetails: { ... } } }
 *
 * Read order on cold start:
 *   1. In-memory Zustand cache
 *   2. `LocalCache.workFlow` (populated by either Vue or React)
 *   3. Network: POST /v1/users/workflows/workflow/search
 */
import { create } from 'zustand';

import { getWorkflowData } from '../api/workflowApi';
import type { WorkflowResponse } from '../types/workflow';
import { logger } from '../utils/logger';
import {
  VUE_CACHE_STORES,
  readVueCache,
  writeVueCache,
  clearVueCache,
} from '../utils/vueLocalCache';

const TTL_MS = 60 * 60 * 1000;

/** Shape Vue caches at `LocalCache.workFlow.[id=data].value` — `body.data`. */
interface CachedWorkflowData {
  workflowDetails?: WorkflowResponse;
}

interface WorkflowCacheEntry {
  data: CachedWorkflowData;
  fetchedAt: number;
}

interface WorkflowStoreState {
  cache: WorkflowCacheEntry | null;
  loading: boolean;
  error: string | null;
  inflight: Promise<WorkflowResponse> | null;
  setCache: (entry: WorkflowCacheEntry | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInflight: (p: Promise<WorkflowResponse> | null) => void;
}

export const useWorkflowStore = create<WorkflowStoreState>((set) => ({
  cache: null,
  loading: false,
  error: null,
  inflight: null,
  setCache: (entry) => set({ cache: entry }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setInflight: (p) => set({ inflight: p }),
}));

const EMPTY_MAP: WorkflowResponse = {};

const unwrap = (body: CachedWorkflowData | null | undefined): WorkflowResponse =>
  body?.workflowDetails ?? EMPTY_MAP;

/**
 * Direct equivalent of Vue's `getWorkFlowDetails()` — async, callable from
 * anywhere (effects, route guards, utility modules), and cached.
 *
 * Returns the workflow map keyed by workflowId. Consumers access:
 *   `details[workflowId]?.workflowDetails?.stageDetails?.[stageId]`
 *
 * `force=true` bypasses BOTH the in-memory and IDB caches, fires the
 * network request, and overwrites both layers with the response. Used by
 * the version-gated dependency step when `/v1/common/master/version`
 * reports the `workflow` entity as changed.
 */
export async function getWorkFlowDetails(
  force = false,
): Promise<WorkflowResponse> {
  const state = useWorkflowStore.getState();

  if (
    !force
    && state.cache
    && Date.now() - state.cache.fetchedAt < TTL_MS
  ) {
    return unwrap(state.cache.data);
  }
  // A force-refresh callsite must not piggy-back on an in-flight cache
  // read — otherwise the stale promise resolves first and the caller
  // never sees the new data. Joining is safe only when this caller is
  // happy with whatever the in-flight request returns.
  if (!force && state.inflight) return state.inflight;

  const promise = (async (): Promise<WorkflowResponse> => {
    state.setLoading(true);
    state.setError(null);
    try {
      // 1. SHARED disk cache — written by Vue OR by us on a previous run.
      //    Skipped entirely when `force=true` so a version-changed entity
      //    actually hits the network.
      if (!force && !state.cache) {
        const cached = await readVueCache<CachedWorkflowData>(
          VUE_CACHE_STORES.workFlow,
        );
        if (cached?.workflowDetails) {
          // Vue does not stamp `fetchedAt`; treat any present cache entry
          // as fresh for the in-memory TTL window. The next API hit will
          // overwrite both.
          state.setCache({ data: cached, fetchedAt: Date.now() });
          return unwrap(cached);
        }
      }

      // 2. Network: POST /v1/users/workflows/workflow/search
      const body = await getWorkflowData();
      const cacheable: CachedWorkflowData = {
        workflowDetails: body?.data?.workflowDetails ?? {},
      };
      state.setCache({ data: cacheable, fetchedAt: Date.now() });

      // 3. Mirror Vue's write — store `body.data` (i.e. cacheable) at
      //    `LocalCache.workFlow.[id=data].value` so Vue picks it up too.
      await writeVueCache(VUE_CACHE_STORES.workFlow, cacheable);

      return unwrap(cacheable);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logger.error('[workflow-store] fetch failed', { err, force });
      state.setError(message);
      return EMPTY_MAP;
    } finally {
      state.setLoading(false);
      state.setInflight(null);
    }
  })();

  state.setInflight(promise);
  return promise;
}

/** Drops both the in-memory and shared LocalCache entries. */
export async function invalidateWorkflowCache(): Promise<void> {
  useWorkflowStore.setState({ cache: null, error: null });
  await clearVueCache(VUE_CACHE_STORES.workFlow);
}
